import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { CombinedOrderRequest, OrderRequest } from '@zero/shared/util-models';
import { firstValueFrom } from 'rxjs';
import { tradeActions } from '../+state/trade.actions';
import { tradeFeature } from '../+state/trade.reducers';
import {
  selectCombinedFractionalOrder,
  selectCombinedMarketOrder,
  selectEstimatedBuySum,
  selectEstimatedSellSum,
  selectEstimatedSum,
  selectHasNotEnoughBuyingPower,
  selectIsFractionOrderOnly,
  selectMultiOrderBuyList,
  selectMultiOrderErrors,
  selectMultiOrderSellList,
  selectShouldDisplayEstimatedBuySum,
  selectShouldDisplayEstimatedSellSum,
  selectShouldDisplaySum,
  selectValidForm,
} from '../+state/trade.selectors';
import { MultiOrderInstrument } from '../entities/multi-order-instrument';
import { UserExperienceData } from '../entities/user-experience-data.entity';

@Injectable({ providedIn: 'root' })
export class TradeFacade {
  readonly #store = inject(Store);
  readonly #customerStateFacade = inject(CustomerStateFacade);

  public holidays: string[] = [];
  public readonly holidays$ = this.#store.select(tradeFeature.selectHolidays);
  public readonly tradingDayInfo$ = this.#store.select(tradeFeature.selectTradingDayInfo);
  public readonly shouldDisplayMultiOrderConsent$ = this.#store.select(tradeFeature.selectShouldDisplayMultiOrderConsent);
  public readonly multiOrderInstruments$ = this.#store.select(tradeFeature.selectMultiOrderInstruments);
  public readonly multiOrderBuyList$ = this.#store.select(selectMultiOrderBuyList);
  public readonly multiOrderSellList$ = this.#store.select(selectMultiOrderSellList);
  public readonly buyingPower$ = this.#store.select(tradeFeature.selectBuyingPower);
  public readonly estimatedBuySum$ = this.#store.select(selectEstimatedBuySum);
  public readonly estimatedSellSum$ = this.#store.select(selectEstimatedSellSum);
  public readonly estimatedSum$ = this.#store.select(selectEstimatedSum);
  public readonly shouldDisplaySum$ = this.#store.select(selectShouldDisplaySum);
  public readonly shouldDisplayEstimatedSellSum$ = this.#store.select(selectShouldDisplayEstimatedSellSum);
  public readonly shouldDisplayEstimatedBuySum$ = this.#store.select(selectShouldDisplayEstimatedBuySum);
  public readonly validForm$ = this.#store.select(selectValidForm);
  public readonly validating$ = this.#store.select(tradeFeature.selectVerifyingMultiOrder);
  public readonly validateSuccess$ = this.#store.select(tradeFeature.selectVerifyMultiOrderSuccess);
  public readonly hasNotEnoughBuyingPower$ = this.#store.select(selectHasNotEnoughBuyingPower);
  public readonly sending$ = this.#store.select(tradeFeature.selectSendingMultiOrder);
  public readonly sendSuccess$ = this.#store.select(tradeFeature.selectSendingMultiOrderSuccess);
  public readonly multiOrderErrors$ = this.#store.select(selectMultiOrderErrors);
  public readonly multiOrderConsentConfirmed$ = this.#store.select(tradeFeature.selectMultiOrderConsentConfirmed);
  public readonly loadingMultiOrderInstruments$ = this.#store.select(tradeFeature.selectLoadingMultiOrderInstruments);
  public readonly loadingExante$ = this.#store.select(tradeFeature.selectLoadingExante);
  public readonly exante$ = this.#store.select(tradeFeature.selectExante);
  public readonly isOrderVerified$ = this.#store.select(tradeFeature.selectIsOrderVerified);
  public readonly isCombinedOrderVerified$ = this.#store.select(tradeFeature.selectIsCombinedOrderVerified);
  public readonly combinedFractionalOrder$ = this.#store.select(selectCombinedFractionalOrder);
  public readonly combinedMarketOrder$ = this.#store.select(selectCombinedMarketOrder);
  public readonly isFractionalOrderOnly$ = this.#store.select(selectIsFractionOrderOnly);
  public readonly isOrderPlaced$ = this.#store.select(tradeFeature.selectIsOrderPlaced);
  public readonly isPlacingOrder$ = this.#store.select(tradeFeature.selectIsPlacingOrder);
  public readonly knowledgeAndExperience$ = this.#store.select(tradeFeature.selectKnowledgeAndExperience);
  public readonly sendingKnowledgeAndExperience$ = this.#store.select(tradeFeature.selectSendingKnowledgeAndExperience);
  public readonly sentKnowledgeAndExperienceSuccessfully$ = this.#store.select(tradeFeature.selectSentKnowledgeAndExperienceSuccessfully);

  constructor() {
    this.holidays$.subscribe((holidays) => (this.holidays = holidays));
  }

  public initMultiOrder = async () => {
    this.loadHolidays();
    await this.loadMultiOrderConsent();
    this.loadBuyingPower();
  };

  public loadHolidays = () => this.#store.dispatch(tradeActions.loadHolidays());
  public loadTradingDayInfo = (isin: string) => this.#store.dispatch(tradeActions.loadTradingDayInfo({ isin }));

  public loadMultiOrderConsent = async () => {
    await firstValueFrom(this.#customerStateFacade.loadCustomerBaseData(true));
    return this.#store.dispatch(tradeActions.loadMultiOrderConsent());
  };

  public confirmMultiOrderConsent = () => this.#store.dispatch(tradeActions.confirmMultiOrderConsent());

  public addMultiOrderInstruments = (instruments: MultiOrderInstrument[]) =>
    this.#store.dispatch(tradeActions.addMultiOrderInstruments({ instruments }));

  public addMultiOrderInstrument = (instrument: MultiOrderInstrument) =>
    this.#store.dispatch(tradeActions.addMultiOrderInstrument({ instrument }));

  public removeMultiOrderInstrument = (instrument: MultiOrderInstrument) =>
    this.#store.dispatch(tradeActions.removeMultiOrderInstrument({ instrument }));

  public clearMultiOrder = () => this.#store.dispatch(tradeActions.clearMultiOrder());

  public updateQuantity = (instrument: MultiOrderInstrument, quantity: number) =>
    this.#store.dispatch(tradeActions.updateQuantity({ instrument, quantity: quantity }));

  public loadBuyingPower = () => this.#store.dispatch(tradeActions.loadBuyingPower());

  public validateMultiOrder = () => this.#store.dispatch(tradeActions.validateMultiOrder());

  public resetMultiOrderVerification = () => this.#store.dispatch(tradeActions.resetMultiOrderVerification());

  public confirmMultiOrderNotice = (instrument: MultiOrderInstrument) =>
    this.#store.dispatch(tradeActions.confirmMultiOrderInstrumentNotice({ instrument }));

  public sendMultiOrder = () => this.#store.dispatch(tradeActions.sendMultiOrder());

  public loadExante = () => this.#store.dispatch(tradeActions.loadExante());

  public verifyOrder = (orderData: OrderRequest): void => this.#store.dispatch(tradeActions.verifyOrder({ orderData }));

  public validateCombinedOrder = (combinedOrder: CombinedOrderRequest) =>
    this.#store.dispatch(tradeActions.validateCombinedOrder({ combinedOrder }));

  public removeFractionalOrder = () => this.#store.dispatch(tradeActions.removeFractionalOrder());

  public placeOrder = (orderRequest: OrderRequest) => this.#store.dispatch(tradeActions.placeOrder({ orderRequest }));

  public placeCombinedOrder = (combinedOrderRequest: CombinedOrderRequest) =>
    this.#store.dispatch(tradeActions.placeCombinedOrder({ combinedOrderRequest }));

  public reset = () => this.#store.dispatch(tradeActions.reset());

  public loadKnowledgeAndExperienceForm = (isins: string[]) => this.#store.dispatch(tradeActions.loadKnowledgeAndExperienceForm({ isins }));

  public submitKnowledgeAndExperienceForm = (knowledgeAndExperience: UserExperienceData) =>
    this.#store.dispatch(tradeActions.submitKnowledgeAndExperienceForm({ userExperienceData: knowledgeAndExperience }));
}
